import React, { useContext, useMemo } from 'react';
import { Button, Dropdown, MenuProps, Modal, Select, message } from 'antd';
import * as Icons from '@ant-design/icons';
import { Column } from 'react-table';
import { useNavigate } from 'react-router-dom';
import { StopPropagation } from '@shared/components/stop-propagation';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { useAuth } from '@modules/me';
import { ICashRegisterOperationWithParent } from '../../interfaces';
import { CashRegisterOperationsTableContext } from '../../context';
import { CashRegisterOperationsService } from '../../services';
import { cashRegisterOperationQueryKeys } from '../../utils';
import { useCashRegisterOperationsWithSub } from './use-cash-register-operations-with-sub';

export const useCashRegisterOperationsTableColumns = (): Column<ICashRegisterOperationWithParent>[] => {
  const { handleFetch } = useContext(CashRegisterOperationsTableContext);
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data: operations = [] } = useCashRegisterOperationsWithSub();

  const actionsColumn = useMemo<Column<ICashRegisterOperationWithParent>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }: any) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Düzəliş et',
            icon: <Icons.EditOutlined />,
            disabled: !can('cash_categories.cud'),
            onClick: () => navigate(`/cash-flow/operations/${original.id}/update`, { state: { background: window.location.pathname } }),
          },
          { type: 'divider' as const },
          {
            key: 'delete',
            label: 'Sil',
            icon: <Icons.DeleteOutlined />,
            danger: true,
            disabled: !can('cash_categories.cud'),
            onClick: () =>
              Modal.confirm({
                title: 'Diqqət',
                content: 'Kateqoriyanı silməyə əminsinizmi?',
                okType: 'danger',
                onOk: async () => {
                  const result = await CashRegisterOperationsService.remove([original.id]);
                  if (result.status === 200) {
                    message.success('Kateqoriya silindi.');
                    handleFetch();
                  } else message.error(result.data as string);
                },
              }),
          },
        ];

        return (
          <StopPropagation>
            <Dropdown menu={{ items }} trigger={['hover']}>
              <Button icon={<Icons.MoreOutlined />} size="small" />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [can, handleFetch, navigate],
  );

  const baseColumns = useMemo<Column<ICashRegisterOperationWithParent>[]>(
    () => [
      { ...nextTableColumns.small, Header: 'Kod', id: cashRegisterOperationQueryKeys.id, accessor: (r) => r.id },
      { accessor: (r) => r.name, id: cashRegisterOperationQueryKeys.name, Header: 'Ad' },
      {
        ...nextTableColumns.large,
        accessor: (r) => r.parent?.name,
        id: cashRegisterOperationQueryKeys.parent.id,
        Header: 'Valideyn',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear style={{ width: '100%' }} value={filterValue} onChange={setFilter}>
            {operations.map((op) => (
              <Select.Option key={op.id} value={op.id.toString()}>
                {op.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      { ...nextTableColumns.date, accessor: (r) => r.createdAt, id: cashRegisterOperationQueryKeys.createdAt, Header: 'Yaradılma tarixi' },
    ],
    [operations],
  );

  return useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);
};
